import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Chip,
    Avatar,
    IconButton
} from '@mui/material';
import {
    Flag,
    User,
    ChevronRight,
    AlertTriangle,
    Clock,
    CheckCircle,
    XCircle,
    Calendar
} from 'react-feather';
import { statusColors, defaultStatus } from '../mockData';

const HighPriorityIssues = ({ tickets, count, openTicketDialog }) => {
    // Get staff initials for avatar
    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase();
    };

    // Get status icon component
    const getStatusIcon = (status) => {
        const iconType = statusColors[status]?.icon || defaultStatus.icon;
        switch (iconType) {
            case 'AlertTriangle': return <AlertTriangle size={14} />;
            case 'Clock': return <Clock size={14} />;
            case 'CheckCircle': return <CheckCircle size={14} />;
            case 'XCircle': return <XCircle size={14} />;
            case 'Calendar': return <Calendar size={14} />;
            default: return <AlertTriangle size={14} />;
        }
    };

    // Get status color
    const getStatusColor = (status) => {
        return statusColors[status] || defaultStatus;
    };

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: '10px',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                overflow: 'hidden',
                height: '100%'
            }}
        >
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.08)', bgcolor: '#F9FAFB' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                        <Flag size={16} style={{ marginRight: 8, color: '#EF4444' }} />
                        High Priority Issues
                    </Typography>
                    <Chip
                        label={count}
                        color="error"
                        size="small"
                        sx={{ fontWeight: 600 }}
                    />
                </Box>
            </Box>
            <Box sx={{ p: 0 }}>
                {tickets.slice(0, 5).map((ticket) => (
                    <Box
                        key={ticket.id}
                        sx={{
                            p: 2,
                            borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                            '&:last-child': { borderBottom: 'none' },
                            '&:hover': { bgcolor: '#F9FAFB', cursor: 'pointer' },
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                        onClick={() => openTicketDialog(ticket)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar
                                sx={{
                                    width: 32,
                                    height: 32,
                                    fontSize: '0.875rem',
                                    mr: 1.5,
                                    bgcolor: '#EF4444'
                                }}
                            >
                                {getInitials(ticket.tenant?.name)}
                            </Avatar>
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {ticket.title}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                    <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>
                                        {ticket.id}
                                    </Typography>
                                    <Chip
                                        icon={getStatusIcon(ticket.status)}
                                        label={ticket.status}
                                        size="small"
                                        sx={{
                                            backgroundColor: getStatusColor(ticket.status).bg,
                                            color: getStatusColor(ticket.status).color,
                                            borderRadius: '4px',
                                            fontSize: '0.625rem',
                                            fontWeight: 500,
                                            height: 20
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {ticket.assignedTo ? (
                                <Chip
                                    avatar={<Avatar sx={{ bgcolor: '#10B981' }}>{getInitials(ticket.assignedTo.name)}</Avatar>}
                                    label={ticket.assignedTo.name}
                                    variant="outlined"
                                    size="small"
                                    sx={{ mr: 1, height: 24 }}
                                />
                            ) : (
                                <Chip
                                    icon={<User size={12} />}
                                    label="Unassigned"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        mr: 1,
                                        color: '#EF4444',
                                        borderColor: '#EF4444',
                                        height: 24
                                    }}
                                />
                            )}
                            <IconButton size="small">
                                <ChevronRight size={16} />
                            </IconButton>
                        </Box>
                    </Box>
                ))}
                {tickets.length === 0 && (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="textSecondary">
                            No high priority issues at the moment
                        </Typography>
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default HighPriorityIssues;